import React, { useState, useEffect } from "react";
import Select from "react-select";
import { updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import {
  requireTenantBandContext,
  tenantSheetMusicDocRef,
  tenantStoragePath,
} from "../tenantContext";

const keyOptions = [
  { value: "C",   label: "C (Major)"   },
  { value: "C#",  label: "C# (Major)"  },
  { value: "D",   label: "D (Major)"   },
  { value: "D#",  label: "D# (Major)"  },
  { value: "E",   label: "E (Major)"   },
  { value: "F",   label: "F (Major)"   },
  { value: "F#",  label: "F# (Major)"  },
  { value: "G",   label: "G (Major)"   },
  { value: "G#",  label: "G# (Major)"  },
  { value: "A",   label: "A (Major)"   },
  { value: "A#",  label: "A# (Major)"  },
  { value: "B",   label: "B (Major)"   },
  { value: "Cm",  label: "Cm (Minor)"  },
  { value: "C#m", label: "C#m (Minor)" },
  { value: "Dm",  label: "Dm (Minor)"  },
  { value: "D#m", label: "D#m (Minor)" },
  { value: "Em",  label: "Em (Minor)"  },
  { value: "Fm",  label: "Fm (Minor)"  },
  { value: "F#m", label: "F#m (Minor)" },
  { value: "Gm",  label: "Gm (Minor)"  },
  { value: "G#m", label: "G#m (Minor)" },
  { value: "Am",  label: "Am (Minor)"  },
  { value: "A#m", label: "A#m (Minor)" },
  { value: "Bm",  label: "Bm (Minor)"  },
];


export default function EditSongModal({ song, tenantContext, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: song.title || "",
    artist: song.artist || "",
    key: song.key || "",
    bpm: song.bpm || "",
    pdfUrl: song.pdfUrl || "",
  });
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf" || file.size > 5 * 1024 * 1024) {
      setError("Invalid file. Please upload a PDF smaller than 5MB.");
      return;
    }
    setIsUploading(true);
    setError("");
    try {
      const tenant = requireTenantBandContext(tenantContext);
      const storageRef = ref(storage, tenantStoragePath(tenant, "sheet_music", file.name));
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setForm((prev) => ({ ...prev, pdfUrl: downloadURL }));
      setUploadedFileName(file.name.replace(/\.pdf$/i, ""));
    } catch (err) {
      console.error("Error uploading PDF:", err);
      setError("Failed to upload PDF. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.artist.trim()) {
      setError("Title and artist are required.");
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      const tenant = requireTenantBandContext(tenantContext);
      await updateDoc(tenantSheetMusicDocRef(tenant, song.id), form);
      onSaved({ id: song.id, ...form });
      onClose();
    } catch (err) {
      console.error("Error saving song:", err);
      setError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-sm font-medium text-text-primary">Edit Song</h2>

        <div className="flex flex-col gap-3">
          {/* Title */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-text-secondary">Title <span className="text-danger">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              className="w-full"
            />
          </div>

          {/* Artist */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-text-secondary">Artist <span className="text-danger">*</span></label>
            <input
              type="text"
              value={form.artist}
              onChange={(e) => setForm((prev) => ({ ...prev, artist: e.target.value }))}
              className="w-full"
            />
          </div>

          {/* Key */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-text-secondary">Key <span className="normal-case text-text-secondary/50">(optional)</span></label>
            <Select
              options={keyOptions}
              value={keyOptions.find((o) => o.value === form.key) ?? null}
              onChange={(opt) => setForm((prev) => ({ ...prev, key: opt?.value ?? "" }))}
              placeholder="Select a key"
              classNamePrefix="react-select"
            />
          </div>

          {/* BPM */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-text-secondary">BPM <span className="normal-case text-text-secondary/50">(optional)</span></label>
            <input
              type="number"
              min={40}
              max={300}
              value={form.bpm}
              onChange={(e) => setForm((prev) => ({ ...prev, bpm: e.target.value }))}
              className="w-full"
            />
          </div>

          {/* PDF */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-text-secondary">PDF <span className="text-danger">*</span></label>
            {uploadedFileName && (
              <p className="text-xs text-text-secondary">Uploaded: {uploadedFileName}</p>
            )}
            <input
              type="file"
              accept=".pdf"
              disabled={isUploading}
              onChange={handleFileChange}
              className="text-xs text-text-secondary"
            />
            {isUploading && <p className="text-xs text-accent-blue">Uploading PDF...</p>}
          </div>
        </div>

        {error && <p className="mt-3 text-xs text-danger">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border-subtle text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isUploading || isSaving}
            className="border-transparent bg-accent-blue px-3 py-1 text-xs text-white hover:bg-accent-blue/85 disabled:opacity-50"
          >
            {isSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
